/**
 * Unified Light Manager - Fixed Pool Architecture
 * 
 * CRITICAL: Babylon.js recompiles shaders when the number of enabled lights changes.
 * Solution: Pre-create exactly N lights at startup, keep them ALL enabled forever.
 * We just change their properties (position, intensity, color) to "move" them around.
 * 
 * Architecture:
 * - At init, create exactly maxLights pool lights (all enabled, intensity=0)
 * - Map lights and combat effects register as "requests" (data only, no actual Babylon Light)
 * - Each frame, the N pool lights copy properties from the N highest-priority requests
 * - Unused pool lights have intensity=0 (still enabled, just invisible)
 * 
 * This guarantees:
 * - Light count NEVER changes after initialization
 * - No shader recompilation ever
 * - Clean priority-based light management
 */

import { Scene, PointLight, SpotLight, Vector3, Color3, Light, ShadowGenerator, AbstractMesh } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { EntityRegistry } from '../core/EntityRegistry';
import { EntityType } from '../types/EntityTypes';

// ============================================================================
// Types
// ============================================================================

/** Callback for per-frame light updates */
export type LightUpdateCallback = (deltaTime: number, handle: LightHandle) => void;

/** A request for light - just data, no actual Babylon Light */
export interface LightRequest {
  id: string;
  position: Vector3;
  color: Color3;
  intensity: number;
  range: number;
  isCombat: boolean;
  /** Light type: 'point' or 'spot' */
  lightType: 'point' | 'spot';
  /** SpotLight direction (only used if lightType='spot') */
  direction?: Vector3;
  /** SpotLight angle in radians (only used if lightType='spot') */
  angle?: number;
  /** SpotLight exponent/falloff (only used if lightType='spot') */
  exponent?: number;
  /** Per-frame update callback */
  onUpdate?: LightUpdateCallback;
  /** Whether shadows are enabled for this request */
  shadowEnabled?: boolean;
}

export interface LightHandle {
  id: string;
  /** The underlying pool light (for parenting to meshes). May be null if no pool slot available. */
  light: PointLight | SpotLight | null;
  /** Update the light request properties */
  update(options: { position?: Vector3; color?: Color3; intensity?: number; range?: number; direction?: Vector3 }): void;
  /** Release the light request */
  release(): void;
}

/** Pool light with its current assignment */
interface PoolLight {
  light: PointLight | SpotLight;
  lightType: 'point' | 'spot';
  assignedRequestId: string | null;
  /** ShadowGenerator for spotlights (null for point lights) */
  shadowGenerator: ShadowGenerator | null;
}

// ============================================================================
// Implementation
// ============================================================================

class UnifiedLightManagerImpl {
  private scene: Scene | null = null;
  private maxLights: number = 8;
  
  // Fixed pool of actual Babylon lights (never changes after init)
  private pool: PoolLight[] = [];
  
  // Light requests (data only)
  private requests: Map<string, LightRequest> = new Map();
  
  // Player position for distance-based priority
  private playerPosition: Vector3 = Vector3.Zero();
  
  // Cull distance
  private cullDistance: number = 50;
  
  // ID generation
  private nextRequestId = 0;
  
  // ============================================================================
  // Initialization
  // ============================================================================
  
  /**
   * Initialize the light manager and create the fixed pool.
   * Call this once after scene creation, BEFORE loading the map.
   */
  initialize(scene: Scene, maxLights: number): void {
    // If we have stale state from a previous (now disposed) scene, clean up
    if (this.scene && this.scene.isDisposed) {
      logger.core.log('💡 UnifiedLightManager: Clearing stale state from disposed scene');
      this.pool = [];
      this.requests.clear();
      this.scene = null;
    }
    
    if (this.scene) {
      logger.core.warn('UnifiedLightManager already initialized');
      return;
    }
    
    this.scene = scene;
    this.maxLights = maxLights;
    
    // Block material dirty mechanism during pool creation
    const wasBlocked = scene.blockMaterialDirtyMechanism;
    scene.blockMaterialDirtyMechanism = true;
    
    // Pre-create ALL pool lights - they stay enabled forever
    // Create a mix: half PointLights, half SpotLights for flexibility
    const numSpotLights = Math.ceil(maxLights / 2);
    const numPointLights = maxLights - numSpotLights;
    
    // Create PointLights first
    for (let i = 0; i < numPointLights; i++) {
      const light = new PointLight(`pool_light_point_${i}`, Vector3.Zero(), scene);
      light.intensity = 0; // Invisible but enabled
      light.range = 10;
      light.diffuse = Color3.White();
      // CRITICAL: Keep enabled forever - never call setEnabled(false)!
      light.setEnabled(true);
      
      this.pool.push({
        light,
        lightType: 'point',
        assignedRequestId: null,
        shadowGenerator: null // Point lights don't cast shadows in our system
      });
    }
    
    // Create SpotLights
    for (let i = 0; i < numSpotLights; i++) {
      const light = new SpotLight(
        `pool_light_spot_${i}`,
        Vector3.Zero(),
        Vector3.Down(), // Default direction
        Math.PI / 3, // Default angle (60 degrees)
        2, // Default exponent
        scene
      );
      light.intensity = 0; // Invisible but enabled
      light.range = 10;
      light.diffuse = Color3.White();
      // CRITICAL: Keep enabled forever - never call setEnabled(false)!
      light.setEnabled(true);
      
      // Create ShadowGenerator for this spotlight
      // It will be dormant (no casters) until a request enables shadows
      const shadowGenerator = new ShadowGenerator(1024, light);
      shadowGenerator.usePercentageCloserFiltering = true;
      shadowGenerator.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
      shadowGenerator.bias = 0.005;
      shadowGenerator.normalBias = 0.02;
      shadowGenerator.frustumEdgeFalloff = 1.0;
      // Enable transparent shadows (respects material alpha for cutout shadows)
      shadowGenerator.transparencyShadow = true;
      shadowGenerator.enableSoftTransparentShadow = true;
      // Start with no casters - they'll be added when a request enables shadows
      
      this.pool.push({
        light,
        lightType: 'spot',
        assignedRequestId: null,
        shadowGenerator
      });
    }
    
    // Restore dirty mechanism
    scene.blockMaterialDirtyMechanism = wasBlocked;
    
    logger.core.log(`💡 UnifiedLightManager: Created fixed pool of ${maxLights} lights (${numPointLights} point, ${numSpotLights} spot, all enabled)`);
  }
  
  isInitialized(): boolean {
    // Check if we have a scene AND it's still valid (not disposed)
    // Babylon.js scenes get disposed on hot reload
    return this.scene !== null && !this.scene.isDisposed;
  }
  
  // ============================================================================
  // Light Request API
  // ============================================================================
  
  /**
   * Register a map light as a request.
   * Call this for each light found in the glTF. The original Babylon Light should be DISPOSED
   * after extracting its properties - we use pool lights instead.
   */
  registerMapLight(props: {
    position: Vector3;
    color: Color3;
    intensity: number;
    range: number;
    lightType?: 'point' | 'spot';
    direction?: Vector3;
    angle?: number;
    exponent?: number;
  }): string {
    const id = `map_${this.nextRequestId++}`;
    
    this.requests.set(id, {
      id,
      position: props.position.clone(),
      color: props.color.clone(),
      intensity: props.intensity,
      range: props.range,
      isCombat: false,
      lightType: props.lightType ?? 'point',
      direction: props.direction?.clone(),
      angle: props.angle,
      exponent: props.exponent
    });
    
    return id;
  }
  
  /**
   * Finalize map light registration - call after all map lights are registered.
   * This triggers the pool assignment so lights actually become visible.
   */
  finalizeMapLights(): void {
    this.reassignPoolLights();
  }
  
  /**
   * Unregister a map light request.
   */
  unregisterMapLight(id: string): void {
    this.requests.delete(id);
  }
  
  /**
   * Acquire a combat light slot.
   * Returns a handle to update and release the light.
   */
  acquireCombatLight(props: {
    position: Vector3;
    color?: Color3;
    intensity?: number;
    range?: number;
    onUpdate?: LightUpdateCallback;
  }): LightHandle {
    const id = `combat_${this.nextRequestId++}`;
    
    this.requests.set(id, {
      id,
      position: props.position.clone(),
      color: props.color?.clone() ?? Color3.White(),
      intensity: props.intensity ?? 1.0,
      range: props.range ?? 5.0,
      isCombat: true,
      lightType: 'point', // Combat lights are always point lights
      onUpdate: props.onUpdate
    });
    
    // Force immediate reassignment so the handle can find its pool light
    this.reassignPoolLights();
    
    // Find the pool light assigned to this request
    const poolEntry = this.pool.find(p => p.assignedRequestId === id);
    
    const handle: LightHandle = {
      id,
      light: poolEntry?.light ?? null,
      update: (opts) => this.updateRequest(id, opts),
      release: () => this.releaseRequest(id)
    };
    
    this.handles.set(id, handle);
    return handle;
  }
  
  /**
   * Acquire a spotlight slot for dynamic effects (like cave_light trigger).
   * Returns a handle to update and release the light.
   */
  acquireSpotLight(props: {
    position: Vector3;
    direction: Vector3;
    color?: Color3;
    intensity?: number;
    range?: number;
    angle?: number;
    exponent?: number;
    onUpdate?: LightUpdateCallback;
    /** Enable shadows (only casts from characters and dynamic objects, not level geometry) */
    shadowEnabled?: boolean;
  }): LightHandle {
    const id = `dynamic_spot_${this.nextRequestId++}`;
    
    this.requests.set(id, {
      id,
      position: props.position.clone(),
      color: props.color?.clone() ?? Color3.White(),
      intensity: props.intensity ?? 1.0,
      range: props.range ?? 30.0,
      isCombat: true, // High priority like combat lights
      lightType: 'spot',
      direction: props.direction.clone(),
      angle: props.angle ?? Math.PI / 3,
      exponent: props.exponent ?? 2,
      onUpdate: props.onUpdate,
      shadowEnabled: props.shadowEnabled ?? false
    });
    
    // Force immediate reassignment so the handle can find its pool light
    this.reassignPoolLights();
    
    // Find the pool light assigned to this request
    const poolEntry = this.pool.find(p => p.assignedRequestId === id);
    
    // If shadows are enabled, set up shadow casters and receivers
    if (props.shadowEnabled && poolEntry?.shadowGenerator) {
      this.refreshShadowCasters(poolEntry.shadowGenerator);
      
      // Enable shadow receiving on all scene meshes (level geometry)
      if (this.scene) {
        for (const mesh of this.scene.meshes) {
          if (!mesh.metadata?.isDebug && !mesh.metadata?.noShadows) {
            mesh.receiveShadows = true;
          }
        }
      }
    }
    
    logger.core.log(`💡 UnifiedLightManager: Acquired spotlight ${id}, assigned to pool: ${poolEntry?.light.name ?? 'NONE'}`);
    
    const handle: LightHandle = {
      id,
      light: poolEntry?.light ?? null,
      update: (opts) => this.updateRequest(id, opts),
      release: () => this.releaseRequest(id)
    };
    
    this.handles.set(id, handle);
    return handle;
  }
  
  /**
   * Get the pool light currently assigned to a request ID.
   * The assignment may change between frames!
   */
  getPoolLightForRequest(requestId: string): PointLight | SpotLight | null {
    const poolEntry = this.pool.find(p => p.assignedRequestId === requestId);
    return poolEntry?.light ?? null;
  }
  
  private updateRequest(id: string, opts: { position?: Vector3; color?: Color3; intensity?: number; range?: number; direction?: Vector3 }): void {
    const request = this.requests.get(id);
    if (!request) return;
    
    if (opts.position) request.position.copyFrom(opts.position);
    if (opts.color) request.color = opts.color.clone();
    if (opts.intensity !== undefined) request.intensity = opts.intensity;
    if (opts.range !== undefined) request.range = opts.range;
    if (opts.direction && request.lightType === 'spot') request.direction = opts.direction.clone();
    
    // If this request is currently assigned to a pool light, update it immediately
    const poolEntry = this.pool.find(p => p.assignedRequestId === id);
    if (poolEntry) {
      this.applyRequestToPoolLight(poolEntry, request);
    }
  }
  
  private releaseRequest(id: string): void {
    this.requests.delete(id);
    this.handles.delete(id);
    // Pool light will be freed on next reassign
  }
  
  // ============================================================================
  // Per-Frame Update
  // ============================================================================
  
  // Track delta time for onUpdate callbacks
  private lastUpdateTime: number = 0;
  
  // Store handles for callback invocation
  private handles: Map<string, LightHandle> = new Map();
  
  /**
   * Update player position, reassign pool lights, and call onUpdate callbacks.
   * Call this each frame.
   */
  update(playerPosition: Vector3): void {
    this.playerPosition = playerPosition;
    this.reassignPoolLights();
    
    // Calculate delta time
    const now = performance.now() / 1000;
    const deltaTime = this.lastUpdateTime > 0 ? now - this.lastUpdateTime : 0.016;
    this.lastUpdateTime = now;
    
    // Call onUpdate for all requests that have callbacks
    for (const request of this.requests.values()) {
      if (request.onUpdate) {
        const handle = this.handles.get(request.id);
        if (handle) {
          request.onUpdate(deltaTime, handle);
        }
      }
    }
  }
  
  setCullDistance(distance: number): void {
    this.cullDistance = distance;
  }
  
  /**
   * Reassign pool lights to highest-priority requests.
   */
  private reassignPoolLights(): void {
    // Build priority list from all requests
    const prioritized: { request: LightRequest; priority: number; distance: number }[] = [];
    
    for (const request of this.requests.values()) {
      const dist = Vector3.Distance(request.position, this.playerPosition);
      const effectiveDist = Math.max(0, dist - request.range);
      
      // Combat: priority 0, Map: priority = distance
      const priority = request.isCombat ? 0 : 1 + effectiveDist;
      
      prioritized.push({ request, priority, distance: effectiveDist });
    }
    
    // Sort by priority (lower = higher priority)
    prioritized.sort((a, b) => a.priority - b.priority);
    
    // Filter to only in-range requests (combat always in range)
    const inRange = prioritized.filter(p => p.request.isCombat || p.distance <= this.cullDistance);
    
    // Take top N requests
    const activeRequests = inRange.slice(0, this.maxLights);
    const activeIds = new Set(activeRequests.map(r => r.request.id));
    
    // Free pool lights that are assigned to requests no longer active
    for (const poolEntry of this.pool) {
      if (poolEntry.assignedRequestId && !activeIds.has(poolEntry.assignedRequestId)) {
        // Clear this pool light
        poolEntry.light.intensity = 0;
        poolEntry.assignedRequestId = null;
      }
    }
    
    // Assign active requests to pool lights
    for (const { request } of activeRequests) {
      // Already assigned?
      const existing = this.pool.find(p => p.assignedRequestId === request.id);
      if (existing) {
        // Update properties
        this.applyRequestToPoolLight(existing, request);
        continue;
      }
      
      // Find a free pool light - prefer matching type
      let free = this.pool.find(p => p.assignedRequestId === null && p.lightType === request.lightType);
      
      // If no matching type, use any free pool light
      if (!free) {
        free = this.pool.find(p => p.assignedRequestId === null);
      }
      
      if (free) {
        free.assignedRequestId = request.id;
        this.applyRequestToPoolLight(free, request);
      }
      // else: no free pool lights (all budget used)
    }
  }
  
  private applyRequestToPoolLight(poolEntry: PoolLight, request: LightRequest): void {
    poolEntry.light.position.copyFrom(request.position);
    poolEntry.light.diffuse = request.color;
    poolEntry.light.intensity = request.intensity;
    poolEntry.light.range = request.range;
    
    // Apply SpotLight-specific properties if this is a SpotLight pool slot
    if (poolEntry.lightType === 'spot' && poolEntry.light instanceof SpotLight) {
      const spotLight = poolEntry.light as SpotLight;
      
      if (request.lightType === 'spot' && request.direction) {
        // Apply spotlight direction and cone
        spotLight.direction.copyFrom(request.direction);
        spotLight.angle = request.angle ?? Math.PI / 3;
        spotLight.exponent = request.exponent ?? 2;
        
        // Handle shadow enable/disable
        if (poolEntry.shadowGenerator) {
          if (request.shadowEnabled) {
            // Refresh shadow casters when shadows are enabled
            this.refreshShadowCasters(poolEntry.shadowGenerator);
          } else {
            // Clear shadow casters when shadows are disabled
            const shadowMap = poolEntry.shadowGenerator.getShadowMap();
            if (shadowMap?.renderList) {
              shadowMap.renderList.length = 0;
            }
          }
        }
      } else {
        // Point light request on a SpotLight pool slot - make it omnidirectional-ish
        // Use a very wide angle to approximate point light behavior
        spotLight.direction.set(0, -1, 0);
        spotLight.angle = Math.PI; // 180 degrees - full hemisphere
        spotLight.exponent = 0; // No falloff
        
        // Disable shadows for point light emulation
        if (poolEntry.shadowGenerator) {
          const shadowMap = poolEntry.shadowGenerator.getShadowMap();
          if (shadowMap?.renderList) {
            shadowMap.renderList.length = 0;
          }
        }
      }
    }
  }
  
  /**
   * Refresh shadow casters for a shadow generator.
   * Only includes characters and dynamic objects - NOT level geometry.
   */
  private refreshShadowCasters(shadowGenerator: ShadowGenerator): void {
    const shadowMap = shadowGenerator.getShadowMap();
    if (!shadowMap) return;
    
    // Clear existing casters
    shadowMap.renderList = [];
    
    // Get all characters and dynamic objects from EntityRegistry
    const registry = EntityRegistry.getInstance();
    const characters = registry.getByType(EntityType.CHARACTER);
    const dynamicObjects = registry.getByType(EntityType.DYNAMIC_OBJECT);
    const projectiles = registry.getByType(EntityType.PROJECTILE);
    const debris = registry.getByType(EntityType.DEBRIS);
    
    const allDynamic = [...characters, ...dynamicObjects, ...projectiles, ...debris];
    
    let casterCount = 0;
    for (const entity of allDynamic) {
      if (!entity.mesh) continue;
      
      // For characters, entity.mesh is the physics capsule (debug mesh)
      // We need to add child meshes (the actual visual model) as shadow casters
      const isParentDebug = entity.mesh.metadata?.isDebug || entity.mesh.metadata?.noShadows;
      
      if (!isParentDebug) {
        // Parent is not debug, add it and children
        shadowGenerator.addShadowCaster(entity.mesh, true);
        casterCount++;
      } else {
        // Parent is debug (like character capsule), add children only
        entity.mesh.getChildMeshes().forEach((child: AbstractMesh) => {
          if (!child.metadata?.isDebug && !child.metadata?.noShadows && child.getTotalVertices() > 0) {
            shadowGenerator.addShadowCaster(child, true);
            casterCount++;
          }
        });
      }
    }
    
    logger.core.debug(`💡 UnifiedLightManager: Refreshed shadow casters (${casterCount} meshes)`);
  }
  
  /**
   * Call this when entities spawn/despawn to update shadow casters.
   * Only updates spotlights that have shadowEnabled requests.
   */
  public refreshAllShadowCasters(): void {
    for (const poolEntry of this.pool) {
      if (poolEntry.lightType !== 'spot' || !poolEntry.shadowGenerator) continue;
      if (!poolEntry.assignedRequestId) continue;
      
      const request = this.requests.get(poolEntry.assignedRequestId);
      if (request?.shadowEnabled) {
        this.refreshShadowCasters(poolEntry.shadowGenerator);
      }
    }
  }
  
  // ============================================================================
  // Debug / Stats
  // ============================================================================
  
  getStats(): {
    poolSize: number;
    totalRequests: number;
    combatRequests: number;
    mapRequests: number;
    activeSlots: number;
  } {
    let combatRequests = 0;
    let mapRequests = 0;
    
    for (const request of this.requests.values()) {
      if (request.isCombat) combatRequests++;
      else mapRequests++;
    }
    
    const activeSlots = this.pool.filter(p => p.assignedRequestId !== null).length;
    
    return {
      poolSize: this.pool.length,
      totalRequests: this.requests.size,
      combatRequests,
      mapRequests,
      activeSlots
    };
  }
  
  /**
   * Check if a light is a pool light (managed by this system).
   */
  isPoolLight(light: Light): boolean {
    return light.name.startsWith('pool_light_');
  }
  
  /**
   * Get the request assigned to a pool light by name.
   * Returns null if the pool light is unused.
   */
  getAssignedRequestForLight(lightName: string): LightRequest | null {
    const poolEntry = this.pool.find(p => p.light.name === lightName);
    if (!poolEntry || !poolEntry.assignedRequestId) return null;
    return this.requests.get(poolEntry.assignedRequestId) ?? null;
  }
  
  /**
   * Clear all requests (call on level unload).
   */
  clearAllRequests(): void {
    this.requests.clear();
    this.handles.clear();
    // Zero all pool lights and clear shadow casters
    for (const poolEntry of this.pool) {
      poolEntry.light.intensity = 0;
      poolEntry.assignedRequestId = null;
      
      // Clear shadow casters
      if (poolEntry.shadowGenerator) {
        const shadowMap = poolEntry.shadowGenerator.getShadowMap();
        if (shadowMap?.renderList) {
          shadowMap.renderList.length = 0;
        }
      }
    }
  }
  
  // ============================================================================
  // Cleanup
  // ============================================================================
  
  dispose(): void {
    for (const poolEntry of this.pool) {
      // Dispose shadow generator first
      if (poolEntry.shadowGenerator) {
        poolEntry.shadowGenerator.dispose();
      }
      poolEntry.light.dispose();
    }
    this.pool = [];
    this.requests.clear();
    this.handles.clear();
    this.lastUpdateTime = 0;
    this.scene = null;
  }
}

// Singleton export
export const UnifiedLightManager = new UnifiedLightManagerImpl();
